import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Save,
  X,
  User,
  Users,
  Shield,
  CheckCircle,
  XCircle,
  AlertCircle,
  Search,
  Filter,
  Mail,
  Calendar,
  Key
} from 'react-feather';

interface UserRole {
  current: 'treeline_user' | 'admin' | 'owner';
  capabilities: {
    canUseTeaching: boolean;
    canManageUsers: boolean;
    canManageContentSources: boolean;
  };
}

interface UserProfile {
  email: string;
  role: 'treeline_user' | 'admin' | 'owner';
  assignedBy?: string;
  assignedAt: string;
  notes?: string;
}

interface RoleInfo {
  role: 'treeline_user' | 'admin' | 'owner';
  name: string;
  description: string;
  permissions: string[];
}

interface UserManagementResponse {
  success: boolean;
  data?: {
    users: UserProfile[];
  };
  error?: string;
}

interface RoleHierarchyResponse {
  success: boolean;
  data?: {
    roles: RoleInfo[];
  };
  error?: string;
}

interface CurrentUserResponse {
  success: boolean;
  data?: {
    user: {
      email: string;
      name: string;
    };
    role: UserRole;
  };
  error?: string;
}

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [roleHierarchy, setRoleHierarchy] = useState<RoleInfo[]>([]);
  const [currentUser, setCurrentUser] = useState<{ email: string; role: UserRole } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  
  // Modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null);
  
  // Form state
  const [formData, setFormData] = useState({
    email: '',
    role: 'treeline_user' as 'treeline_user' | 'admin' | 'owner',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch current user to check permissions
      const currentUserResponse = await fetch('/api/user/profile');
      if (currentUserResponse.ok) {
        const currentUserData: CurrentUserResponse = await currentUserResponse.json();
        if (currentUserData.success && currentUserData.data) {
          setCurrentUser({
            email: currentUserData.data.user.email,
            role: currentUserData.data.role
          });
          
          // Only proceed if user is Owner
          if (!currentUserData.data.role.capabilities.canManageUsers) {
            setError('Access denied. Owner role required for user management.');
            setLoading(false);
            return;
          }
        }
      }
      
      // Fetch role hierarchy
      const rolesResponse = await fetch('/api/user/roles');
      if (rolesResponse.ok) {
        const rolesData: RoleHierarchyResponse = await rolesResponse.json();
        if (rolesData.success && rolesData.data) {
          setRoleHierarchy(rolesData.data.roles);
        }
      }
      
      // Fetch users - need to create this API endpoint
      await fetchUsers();
      
    } catch (error) {
      console.error('Error fetching user management data:', error);
      setError('Failed to load user management data');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      // We need to create this endpoint in the backend
      const response = await fetch('/api/user/manage');
      if (response.ok) {
        const data: UserManagementResponse = await response.json();
        if (data.success && data.data) {
          setUsers(data.data.users);
        }
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleAddUser = () => {
    setFormData({ email: '', role: 'treeline_user', notes: '' });
    setSelectedUser(null);
    setShowAddModal(true);
  };

  const handleEditUser = (user: UserProfile) => {
    setFormData({
      email: user.email,
      role: user.role,
      notes: user.notes || ''
    });
    setSelectedUser(user);
    setShowEditModal(true);
  };

  const handleDeleteUser = (user: UserProfile) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const submitUserForm = async () => {
    try {
      const url = selectedUser ? `/api/user/manage/${encodeURIComponent(selectedUser.email)}` : '/api/user/manage';
      const method = selectedUser ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          role: formData.role,
          notes: formData.notes
        }),
      });

      if (response.ok) {
        await fetchUsers();
        setShowAddModal(false);
        setShowEditModal(false);
        setFormData({ email: '', role: 'treeline_user', notes: '' });
        setSelectedUser(null);
      } else {
        const errorData = await response.json();
        alert(`Error: ${errorData.error}`);
      }
    } catch (error) {
      console.error('Error submitting user form:', error);
      alert('Failed to save user');
    }
  };

  const confirmDeleteUser = async () => {
    if (!selectedUser) return;
    
    try {
      const response = await fetch(`/api/user/manage/${encodeURIComponent(selectedUser.email)}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        await fetchUsers();
        setShowDeleteModal(false);
        setSelectedUser(null);
      } else {
        const errorData = await response.json();
        alert(`Error: ${errorData.error}`);
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Failed to delete user');
    }
  };

  const getRoleInfo = (role: string) => {
    return roleHierarchy.find(r => r.role === role);
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'owner': return <Key size={16} className="text-yellow-500" />;
      case 'admin': return <Shield size={16} className="text-blue-500" />;
      default: return <User size={16} className="text-gray-500" />;
    }
  };

  const getRoleBadge = (role: string) => {
    const roleInfo = getRoleInfo(role);
    const colors = {
      'owner': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'admin': 'bg-blue-100 text-blue-800 border-blue-200',
      'treeline_user': 'bg-gray-100 text-gray-800 border-gray-200'
    };

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${colors[role as keyof typeof colors]}`}>
        {getRoleIcon(role)}
        {roleInfo?.name || role}
      </span>
    );
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-eddy-blue mx-auto mb-4"></div>
          <p className="text-gray-600">Loading user management...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 m-6">
        <div className="flex items-center gap-3">
          <XCircle className="text-red-500" size={24} />
          <div>
            <h3 className="text-red-800 font-semibold">Access Denied</h3>
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Users className="text-eddy-blue" />
              User Management
            </h1>
            <p className="text-gray-600 mt-1">
              Manage user roles and permissions for Eddy platform access
            </p>
          </div>
          <button
            onClick={handleAddUser}
            className="bg-eddy-blue text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <Plus size={16} />
            Add User
          </button>
        </div>
      </div>

      {/* Role Hierarchy Info */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Key className="text-eddy-green" />
          Role Hierarchy
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {roleHierarchy.map(role => (
            <div key={role.role} className="border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                {getRoleIcon(role.role)}
                <h3 className="font-semibold text-gray-900">{role.name}</h3>
              </div>
              <p className="text-gray-600 text-sm mb-3">{role.description}</p>
              <div className="space-y-1">
                {role.permissions.map(permission => (
                  <div key={permission} className="flex items-center gap-1 text-xs text-gray-500">
                    <CheckCircle size={12} className="text-green-500" />
                    {permission}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-3 text-gray-400" size={16} />
              <input
                type="text"
                placeholder="Search by email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-eddy-blue focus:border-transparent"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Filter size={16} className="text-gray-500" />
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-eddy-blue focus:border-transparent"
            >
              <option value="all">All Roles</option>
              {roleHierarchy.map(role => (
                <option key={role.role} value={role.role}>{role.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Users ({filteredUsers.length})
          </h2>
        </div>
        
        {filteredUsers.length === 0 ? (
          <div className="text-center py-12">
            <Users className="mx-auto text-gray-400 mb-4" size={48} />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Users Found</h3>
            <p className="text-gray-600 mb-4">
              {searchTerm || roleFilter !== 'all' 
                ? 'No users match your search criteria.' 
                : 'No users have been assigned roles yet.'}
            </p>
            {!searchTerm && roleFilter === 'all' && (
              <button
                onClick={handleAddUser}
                className="bg-eddy-blue text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2 mx-auto"
              >
                <Plus size={16} />
                Add First User
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Assigned By
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Assigned Date
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.map((user) => (
                  <tr key={user.email} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Mail size={16} className="text-gray-400 mr-3" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">{user.email}</div>
                          {user.notes && (
                            <div className="text-sm text-gray-500">{user.notes}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getRoleBadge(user.role)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {user.assignedBy || 'System'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center gap-1">
                        <Calendar size={14} />
                        {new Date(user.assignedAt).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => handleEditUser(user)}
                          className="text-eddy-blue hover:text-blue-700 p-1"
                          title="Edit User"
                        >
                          <Edit size={16} />
                        </button>
                        {user.email !== currentUser?.email && (
                          <button
                            onClick={() => handleDeleteUser(user)}
                            className="text-red-600 hover:text-red-800 p-1"
                            title="Delete User"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit User Modal */}
      {(showAddModal || showEditModal) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {selectedUser ? 'Edit User Role' : 'Add User'}
              </h3>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setShowEditModal(false);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={(e) => { e.preventDefault(); submitUserForm(); }}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address
                  </label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    disabled={!!selectedUser}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-eddy-blue focus:border-transparent ${selectedUser ? 'bg-gray-50' : ''}`}
                    placeholder="user@company.com"
                  />
                  {selectedUser && (
                    <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Role
                  </label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value as any }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-eddy-blue focus:border-transparent"
                  >
                    {roleHierarchy.map(role => (
                      <option key={role.role} value={role.role}>
                        {role.name} - {role.description}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes (Optional)
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-eddy-blue focus:border-transparent"
                    placeholder="Additional notes about this user's role assignment..."
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setShowEditModal(false);
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-eddy-blue text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                >
                  <Save size={16} />
                  {selectedUser ? 'Update User' : 'Add User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <AlertCircle className="text-red-500" size={24} />
              <h3 className="text-lg font-semibold text-gray-900">Delete User</h3>
            </div>
            
            <p className="text-gray-600 mb-6">
              Are you sure you want to remove <strong>{selectedUser.email}</strong> from the role system? 
              They will revert to the default Treeline User role.
            </p>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteUser}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"
              >
                <Trash2 size={16} />
                Delete User
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;